/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import truncate from 'lodash/truncate';
import { CLOUDINARY_BASE_VALUES, LOCATION_IMG } from '../../../utils/constants';
import { Icon } from '../../ui/Icon';
import Picture from '../../ui/Picture';
import PriceGenerator from '../../ui/PriceGenerator';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import * as styles from './index.styles';

export const defaultData = {
  ProtonName: '',
  Url: '',
  LocationHeroImage: null,
  IsFeatured: false,
  LocationAddress: '',
  Prices: [],
  LocationBrandImage: null,
  ClosestAirportContent: null,
  ClosestStationContent: null,
  IsPartner: false,
  FallBackImage: {
    value: {
      src: LOCATION_IMG.FIRST.FALLBACK,
    },
  },
};

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 404,
    height: 227,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 418,
    height: 227,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 327,
    height: 184,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 327,
    height: 184,
  },
};

const brandImageSize = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 79,
    aspectRatio: '16:9',
    crop: 'fit',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 79,
    aspectRatio: '16:9',
    crop: 'fit',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 79,
    aspectRatio: '16:9',
    crop: 'fit',
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 79,
    aspectRatio: '16:9',
    crop: 'fit',
  },
};

const renderSolutionItem = (item, i) => {
  if (item.SolutionPrice) {
    return (
      <div css={styles.solutionItemCss} key={`solution-price-${i}`}>
        <span css={styles.solutionItemNameCss}>
          <Text field={item.SolutionName} editable={false} />
        </span>
        <span css={styles.solutionItemPriceCss}>
          <PriceGenerator price={item.SolutionPrice} type="listing" />
        </span>
      </div>
    );
  }
};

const prepareSrc = (image, isBrandImage = false) => {
  if (image.value) {
    image.value.src = isBrandImage
      ? `/${get(image, 'value.src', '')}`
      : get(image, 'value.src', '');
  }
  return image;
};

export class Card extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  componentDidUpdate(prevProps) {
    if (isEqual(prevProps, this.props)) return;

    this.fields = defaultsDeep(this.props, prevProps);
  }

  safeRender() {
    const { t } = this.props;
    const {
      ClosestAirportContent,
      ClosestStationContent,
      FallBackImage,
      IsFeatured,
      IsPartner,
      LocationAddress,
      LocationBrandImage,
      LocationHeroImage,
      Prices,
      ProtonName,
      Url,
    } = this.fields;

    let parsedImage =
      !isEmpty(LocationHeroImage) && JSON.parse(LocationHeroImage);

    let parsedBrandImage =
      !isEmpty(LocationBrandImage) && JSON.parse(LocationBrandImage);

    parsedImage = prepareSrc(parsedImage);
    parsedBrandImage = parsedBrandImage && prepareSrc(parsedBrandImage, true);

    return (
      <a
        data-item="result-card"
        href={Url}
        rel="noopener noreferrer"
        css={styles.cardCss}
      >
        {IsFeatured && <span css={styles.featuredCss}>{t('Featured')}</span>}
        {!!get(parsedImage, 'value') && (
          <Picture
            image={parsedImage}
            imageCss={styles.imageCss}
            imageSizes={imageSizes}
            fallbackAlt={`${t(
              LOCATION_IMG.FIRST.DICTIONARY_KEY,
            )} ${ProtonName}`}
            isClientLink
            fallBack={FallBackImage}
          />
        )}
        <div className="result-cards-info" css={styles.cardInfoCss}>
          <div css={styles.titleAddressCss}>
            <h2 css={styles.cardTitleCss}>
              {truncate(ProtonName, { length: 38 })}
            </h2>
            <p css={styles.cardAddressCss}>
              {truncate(LocationAddress, { length: 88 })}
            </p>
          </div>
          <div css={styles.solutionsItemsWrapperCss}>
            {Prices && Prices.length >= 1 && Prices.map(renderSolutionItem)}
          </div>
          <div css={styles.footerDataCss}>
            <div>
              {ClosestAirportContent && (
                <p css={styles.locationItemCss}>
                  {/* TODO: Change this icon to new icomoon approach */}
                  <Icon customCss={styles.solutionIconCss} name="by-train" />
                  <span>{ClosestAirportContent}</span>
                </p>
              )}
              {ClosestStationContent && (
                <p css={styles.locationItemCss}>
                  {/* TODO: Change this icon to new icomoon approach */}
                  <Icon customCss={styles.solutionIconCss} name="by-plane" />
                  <span>{ClosestStationContent}</span>
                </p>
              )}
            </div>
            {!!get(parsedBrandImage, 'value') && (
              <div>
                <Picture
                  image={parsedBrandImage}
                  imageCss={styles.imageBrandCss}
                  imageSizes={brandImageSize}
                />
                {IsPartner && (
                  <span css={styles.partnerTextCss}>
                    {t('AssociatedPartner')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </a>
    );
  }
}

Card.displayName = 'Card';

Card.defaultProps = { fields: defaultData, t: key => key };

const PriceType = PropTypes.shape({
  SolutionName: PropTypes.shape({
    value: PropTypes.string,
  }),
  SolutionPrice: PropTypes.shape({
    Price: PropTypes.number,
    Currency: PropTypes.string,
    PriceUnit: PropTypes.string,
  }),
});

Card.propTypes = {
  ProtonName: PropTypes.string,
  Url: PropTypes.string,
  LocationHeroImage: PropTypes.object,
  IsFeatured: PropTypes.string,
  LocationAddress: PropTypes.string,
  ClosestAirportContent: PropTypes.string,
  ClosestStationContent: PropTypes.string,
  Prices: PropTypes.arrayOf(PriceType),
  LocationBrandImage: PropTypes.object,
  IsPartner: PropTypes.string,
  FallBackImage: PropTypes.object,
};

export default withNamespaces()(Card);
